/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import {
  findSeriesDataItemInfo,
  getSingletonInsightDvMarkerSeries,
  mergeSeriesDataItem,
  getDataItemValue
} from '../../helper/standardChart';
import { handleMultipleSeries, handleAnimation, isMultipleSeriesWithoutDvMarker } from '../helper';

type InsightData = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的 x 轴指标数据值 */
  xValue: string;
  /** 要标记的 y 轴指标数据值 */
  yValue?: string;
  /** 文本描述 */
  summary: string;
  /** 文本摘要 @see https://datav.iwencai.com/components/paradigm-chart/docs/apis/series/dvMarker/#seriesdvitemstyle */
  textSummary?: unknown;
  /** 点 @see https://echarts.apache.org/zh/option.html#series-bar.markPoint.data */
  point?: unknown;
  /** 点与轴之间的参考线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  axisReferenceLine?: unknown;
  /** 弱化系列 */
  blurSeries?: unknown;
};

const type = 'Point';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markPoint = series.markPoint || {};
        series.markPoint.data = (series.markPoint.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );
        series.markLine = series.markLine || {};
        series.markLine.data = (series.markLine.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
      option.legend.data = option.legend.data?.map(legendItem => {
        delete (legendItem as { __blur?: number }).__blur;
        return legendItem;
      });
    }
  },
  render: {
    onOptionUpdate(context, option, legendIns) {
      const insight = context.insightOption;
      const { linkLegend = false } = insight;
      const { animationDuration = 0 } = insight;
      const isMultipleSeries = isMultipleSeriesWithoutDvMarker(option);

      function render(data: InsightData) {
        const {
          x,
          y,
          xValue,
          yValue,
          summary,
          textSummary: textSummaryOpt = {},
          axisReferenceLine: axisReferenceLineOpt = {},
          blurSeries = {}
        } = data;
        const blurSeriesOpt = JSON.parse(JSON.stringify(blurSeries));
        const dataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue,
          yValue
        });

        if (!dataItemMetaInfo) {
          return;
        }
        const { xAxisIndex, yAxisIndex, seriesIndex, dataItemIndex } = dataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];

        const [xVal, yVal] = [xValue, getDataItemValue(series.data[dataItemIndex])];
        // 适配散点图（yVal：[x,y]包含了坐标）
        const isArray = Array.isArray(yVal);

        series.markPoint = series.markPoint || {};
        series.markPoint.data = series.markPoint.data || [];
        series.markLine = series.markLine || {};
        series.markLine.data = series.markLine.data || [];

        const pointAxisReferenceLine = [
          {
            ...context.util.data.deepMerge(axisReferenceLineOpt, {
              symbol: 'circle'
            }),
            coord: isArray ? yVal : [xVal, yVal]
          },
          {
            ...context.util.data.deepMerge(axisReferenceLineOpt, {
              symbol: 'none',
              name: xVal
            }),
            coord: isArray ? yVal : [xVal, yVal],
            dvGridY: '100%'
          }
        ];
        (pointAxisReferenceLine as unknown as { __insightFlag: string }).__insightFlag =
          INSIGHT_FLAG;

        series.markLine.data.push(pointAxisReferenceLine);

        const markerSeries = getSingletonInsightDvMarkerSeries(option, { xAxisIndex, yAxisIndex });
        const pointSummary = {
          value: isArray ? yVal : [String(xVal), yVal],
          __seriesIndex: seriesIndex,
          itemStyle: context.util.data.deepMerge(
            {
              textStyle: {
                style: {
                  text: summary
                }
              }
            },
            textSummaryOpt
          )
        };
        handleAnimation(series.markLine, animationDuration, markerSeries);

        markerSeries.data.push(pointSummary as unknown as number);
        if (!option.series[seriesIndex].areaStyle) {
          delete blurSeriesOpt.areaStyle;
        }

        if (isMultipleSeries) {
          // TODO 多系列下柱状图的弱化效果不同
          handleMultipleSeries(option, seriesIndex, legendIns!, blurSeriesOpt, linkLegend);
          return;
        }

        // * 弱化主图
        if (series.type === 'bar') {
          series.data = series.data.map((dataItem, index) => {
            if (
              [dataItemIndex].includes(index) ||
              (dataItem as unknown as { __noBlur?: boolean })?.__noBlur
            ) {
              return mergeSeriesDataItem(dataItem, { __noBlur: true });
            }

            return mergeSeriesDataItem(dataItem, blurSeriesOpt);
          });
        }
        if (series.type === 'line') {
          option.series = context.util.data.toArray(option.series);
          option.series[seriesIndex] = context.util.data.deepMerge(series, blurSeriesOpt);
        }
      }

      insight.data.forEach(item => render(item));
    }
  }
});
